import { actionTypes, mutationTypes } from '@/types';
import { uiComponents, uiStates } from '@/tools/viewState';
import { shuffle, getRandomElementsFromArray } from '@/tools/helpers';
import { gameLevels } from '@/tools/consts';
import { setUrlToDataSource } from '@/tools/setUrlToDataSource';

export default {
	state: {
		gameLevel: 'easy',
		numberOfPlayersPlaying: 1,
		memoryCards: null,
		cardsUrl: null,
		flippedCards: 0,
		bubbleOneText: null,
		bubbleTwoText: null,
		playerOneActive: false,
		playerTwoActive: false,
		turnCount: 2,
		playerOneFlippedCount: 0,
		playerTwoFlippedCount: 0,
		playerOneScore: 0,
		playerTwoScore: 0,
		isGameCompleted: false,
		playerOneWon: false,
		playerTwoWon: false,
		cardMetaData: null,
	},
	mutations: {
		[mutationTypes.setMemorygameLevel](state, { gameLevel }) {
			state.gameLevel = gameLevel;
		},
		[mutationTypes.setNumberOfPlayersPlaying](state, { numberOfPlayersPlaying }) {
			state.numberOfPlayersPlaying = numberOfPlayersPlaying;
		},
		[mutationTypes.memoryCards](state, { memoryCards }) {
			state.memoryCards = memoryCards;
		},
		[mutationTypes.cardsUrl](state, { cardsUrl }) {
			state.cardsUrl = cardsUrl;
		},
		[mutationTypes.flippedCards](state, { flippedCard }) {
			state.flippedCards = flippedCard;
		},
		[mutationTypes.bubbleOneText](state, { bubbleOneText }) {
			state.bubbleOneText = bubbleOneText;
		},
		[mutationTypes.bubbleTwoText](state, { bubbleTwoText }) {
			state.bubbleTwoText = bubbleTwoText;
		},
		[mutationTypes.turnCount](state) {
			state.turnCount++;
		},
		[mutationTypes.setActivePlayer](state, { playerOneActive, playerTwoActive }) {
			state.playerOneActive = playerOneActive;
			state.playerTwoActive = playerTwoActive;
		},
		[mutationTypes.setPlayerOneScore](state, { playerOneScore }) {
			state.playerOneScore = playerOneScore;
		},
		[mutationTypes.setPlayerTwoScore](state, { playerTwoScore }) {
			state.playerTwoScore = playerTwoScore;
		},
		[mutationTypes.playerOneFlippedCount](state, { playerOneFlippedCount }) {
			state.playerOneFlippedCount = playerOneFlippedCount;
		},
		[mutationTypes.playerTwoFlippedCount](state, { playerTwoFlippedCount }) {
			state.playerTwoFlippedCount = playerTwoFlippedCount;
		},
		[mutationTypes.playerOneWon](state, { playerOneWon }) {
			state.playerOneWon = playerOneWon;
		},
		[mutationTypes.playerTwoWon](state, { playerTwoWon }) {
			state.playerTwoWon = playerTwoWon;
		},
		[mutationTypes.cardsMetaData](state, { metaData }) {
			state.cardsMetaData = metaData;
		},
		[mutationTypes.restartMemoryGame](state) {
			state.flippedCards = 0;
			state.playerOneActive = false;
			state.playerTwoActive = false;
			state.turnCount = 2;
			state.playerOneFlippedCount = 0;
			state.playerTwoFlippedCount = 0;
			state.playerOneScore = 0;
			state.playerTwoScore = 0;
			state.playerOneWon = false;
			state.playerTwoWon = false;
			state.isGameCompleted = false;
			document.querySelectorAll('.card').forEach((card) => {
				card.classList.remove('flipped', 'matched');
				card.style.removeProperty('visibility');
			});
		},
		[mutationTypes.isGameCompleted](state, { isGameCompleted }) {
			state.isGameCompleted = isGameCompleted;
		},
	},
	actions: {
		[actionTypes.launchMemoryGame]({ commit, getters, rootState }) {
			commit(mutationTypes.viewStateSetUiState, [
				{ id: uiComponents.uiMemoryGame, uiState: uiStates.maximised },
			]);
			const { languageContent } = getters.getArticleByType(
				rootState.gameMode === 'kids' ? 'memoryGameKids' : 'memoryGameAdult'
			);
			const { game } = getters.getArticleByType(
				rootState.gameMode === 'kids' ? 'memoryGameKids' : 'memoryGameAdult'
			);
			const uniqueMediaObjects = game.mediaObjectWithAttribution.reduce((acc, current) => {
				const mediaObject = acc.find((item) => item.source === current.source);
				if (!mediaObject) {
					return acc.concat([current]);
				} else {
					return acc;
				}
			}, []);
			const mediaData = uniqueMediaObjects.map((mediaObject) => {
				return {
					imageUrl: setUrlToDataSource(`../media-${mediaObject.source}/2048`, rootState.dataSourceURL),
					attribution: mediaObject.attribution,
				};
			});
			commit(mutationTypes.cardsMetaData, {
				metaData: mediaData,
			});
			// setting bubble data
			const [lc] = languageContent.filter((lc) => lc.languageISO === getters.currentLanguage);
			const bubbleOneInfo = lc.title.split('|');
			const bubbleTwoInfo = lc.description.split('|');
			commit(mutationTypes.bubbleOneText, {
				bubbleOneText: bubbleOneInfo,
			});
			commit(mutationTypes.bubbleTwoText, {
				bubbleTwoText: bubbleTwoInfo,
			});
		},
		[actionTypes.startMemoryGame](
			{ commit, getters, state, rootState },
			{ gameLevel, numberOfPlayersPlaying }
		) {
			commit(mutationTypes.setMemorygameLevel, {
				gameLevel,
			});
			commit(mutationTypes.setNumberOfPlayersPlaying, {
				numberOfPlayersPlaying,
			});
			const { game } = getters.getArticleByType(
				rootState.gameMode === 'kids' ? 'memoryGameKids' : 'memoryGameAdult'
			);
			const randomArray = getRandomElementsFromArray(
				[...new Set(game.mediaObjects)],
				gameLevels[state.gameLevel]
			);
			const shuffledArray = shuffle([...randomArray, ...randomArray]);
			const urls = shuffledArray.map((element, index) => {
				return {
					imageUrl: setUrlToDataSource(`../media-${element}/2048`, rootState.dataSourceURL),
					index,
					id: element,
				};
			});

			commit(mutationTypes.memoryCards, {
				memoryCards: shuffledArray,
			});
			commit(mutationTypes.cardsUrl, {
				cardsUrl: urls,
			});
		},
		[actionTypes.flipCard]({ commit, state, dispatch }, { event, cardRef, selectedCardIndex }) {
			document.querySelectorAll('.card').forEach((card) => {
				card.classList.remove('notmatched');
			});
			let flippedCardsCount = state.flippedCards;
			flippedCardsCount = ++flippedCardsCount;
			commit(mutationTypes.flippedCards, {
				flippedCard: flippedCardsCount,
			});
			// if only one player is playing
			if (state.numberOfPlayersPlaying === 1) {
				commit(mutationTypes.setActivePlayer, {
					playerOneActive: true,
					playerTwoActive: false,
				});
			}

			if (state.numberOfPlayersPlaying > 1) {
				// incrementing the count to set the active player
				dispatch(actionTypes.setActivePlayer);
			}
			if (state.flippedCards <= 2) {
				if (cardRef.index === selectedCardIndex) {
					event.target.parentElement.classList.add('flipped');
				}
			}
			if (state.flippedCards === 2) {
				const flippedCards = document.querySelectorAll('.flipped:not(.matched)');
				if (flippedCards.length === 2) {
					if (flippedCards[0].getAttribute('data-compare') === flippedCards[1].getAttribute('data-compare')) {
						flippedCards[0].classList.add('matched');
						flippedCards[1].classList.add('matched');
						// this is required becuase customer asked to remove the cards
						setTimeout(() => {
							flippedCards[0].style.visibility = 'hidden';
							flippedCards[1].style.visibility = 'hidden';
							flippedCards[0].classList.remove('notmatched');
							flippedCards[1].classList.remove('notmatched');
						}, 1000);
						// if matched set active player score and giving one more chance
						if (state.playerOneActive) {
							commit(mutationTypes.setPlayerOneScore, {
								playerOneScore: state.playerOneScore + 1,
							});
							setTimeout(() => {
								commit(mutationTypes.turnCount);
								dispatch(actionTypes.setActivePlayer);
							}, 300);
						} else if (state.playerTwoActive) {
							commit(mutationTypes.setPlayerTwoScore, {
								playerTwoScore: state.playerTwoScore + 1,
							});
							setTimeout(() => {
								commit(mutationTypes.turnCount);
								dispatch(actionTypes.setActivePlayer);
							}, 300);
						}
					}
				}
				// flipping the cards back if the cards are not matched
				setTimeout(() => {
					dispatch(actionTypes.flipBackCardsIfNotMatched);
					commit(mutationTypes.turnCount);
					dispatch(actionTypes.setActivePlayer);
				}, 600);

				// incrementing the count to set the active player
				if (state.numberOfPlayersPlaying > 1) {
					dispatch(actionTypes.setActivePlayer);
				}
				// incrementing players flipped count
				if (state.playerOneActive) {
					commit(mutationTypes.playerOneFlippedCount, {
						playerOneFlippedCount: state.playerOneFlippedCount + 1,
					});
				} else if (state.playerTwoActive) {
					commit(mutationTypes.playerTwoFlippedCount, {
						playerTwoFlippedCount: state.playerTwoFlippedCount + 1,
					});
				}
			}
			// clearing the board once the game is over
			if (!document.querySelectorAll('.card:not(.flipped)').length) {
				setTimeout(() => {
					document.querySelectorAll('.card').forEach((card) => {
						card.classList.remove('flipped', 'matched', 'notmatched');
						card.style.removeProperty('visibility');
					});
					commit(mutationTypes.isGameCompleted, { isGameCompleted: true });
					dispatch(actionTypes.setWinningPlayer);
				}, 1000);
			}
		},
		[actionTypes.flipBackCardsIfNotMatched]({ commit }) {
			document.querySelectorAll('.flipped').forEach((card) => {
				if (!card.classList.contains('matched')) {
					card.classList.add('notmatched');
				}
			});
			document.querySelectorAll('.card:not(.matched)').forEach((card) => {
				card.classList.remove('flipped');
			});
			commit(mutationTypes.flippedCards, {
				flippedCard: 0,
			});
		},
		[actionTypes.setActivePlayer]({ commit, state }) {
			if (state.turnCount % 2 === 0) {
				commit(mutationTypes.setActivePlayer, {
					playerOneActive: true,
					playerTwoActive: false,
				});
			} else {
				commit(mutationTypes.setActivePlayer, {
					playerOneActive: false,
					playerTwoActive: true,
				});
			}
		},
		[actionTypes.setWinningPlayer]({ state, dispatch, commit }) {
			if (state.playerOneScore === state.playerTwoScore || state.playerTwoScore === state.playerOneScore) {
				dispatch(actionTypes.evaluateFlipCount);
			} else if (state.playerOneScore > state.playerTwoScore) {
				commit(mutationTypes.playerOneWon, {
					playerOneWon: true,
				});
				commit(mutationTypes.playerTwoWon, {
					playerTwoWon: false,
				});
			} else if (state.playerTwoScore > state.playerOneScore) {
				commit(mutationTypes.playerOneWon, {
					playerOneWon: false,
				});
				commit(mutationTypes.playerTwoWon, {
					playerTwoWon: true,
				});
			}
		},
		[actionTypes.evaluateFlipCount]({ commit, state }) {
			if (state.playerOneFlippedCount < state.playerTwoFlippedCount) {
				commit(mutationTypes.playerOneWon, {
					playerOneWon: true,
				});
				commit(mutationTypes.playerTwoWon, {
					playerTwoWon: false,
				});
			} else if (state.playerTwoFlippedCount < state.playerOneFlippedCount) {
				commit(mutationTypes.playerOneWon, {
					playerOneWon: false,
				});
				commit(mutationTypes.playerTwoWon, {
					playerTwoWon: true,
				});
			} else if (state.playerTwoFlippedCount === state.playerOneFlippedCount) {
				commit(mutationTypes.playerOneWon, {
					playerOneWon: true,
				});
				commit(mutationTypes.playerTwoWon, {
					playerTwoWon: true,
				});
			}
		},
		[actionTypes.restartMemoryGame]({ commit }) {
			commit(mutationTypes.restartMemoryGame);
		},
	},
};
